import * as React from 'react'

import { FilterXIcon } from 'lucide-react'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from 'react-virtualized'

import DeliveryWorkflowCard from '~/client/src/shared/components/ExpandableWorkflowCard/components/DeliveryWorkflowCard'
import {
  GroupHeader,
  GroupHeaderTrigger,
} from '~/client/src/shared/components/GroupHeader'
import { ILWFCCategory } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import Delivery from '~/client/src/shared/models/Delivery'
import { NOOP } from '~/client/src/shared/utils/noop'
import { areArraysEqual } from '~/client/src/shared/utils/util'

import './CompactDeliveriesList.scss'

export interface ICompactRow {
  category?: ILWFCCategory
  delivery?: Delivery
}

interface IProps {
  rows: ICompactRow[]
  highlightedIds?: string[]
  groupingKey?: string
  scrollToIndex?: number
  activeDeliveryId?: string
  onFilterResetClick?: () => void
  collapsedCategories?: Map<string, boolean>
  onCardClick?: (deliveryId: string) => void
  onCategoryCollapsingToggle?: (categoryId: string) => void
}

const NO_SCROLL = -1
const OVERSCAN_ROW_COUNT = 6

const deliveriesMapViewDescriptions = {
  noDeliveries: 'No deliveries matching your filter criteria...',
}

@observer
export default class CompactDeliveriesList extends React.Component<IProps> {
  public static defaultProps = {
    highlightedIds: [],
    scrollToIndex: NO_SCROLL,
    onFilterResetClick: NOOP,
    onCategoryCollapsingToggle: NOOP,
    onCardClick: NOOP,
    collapsedCategories: new Map<string, boolean>(),
    groupingKey: '',
  }

  @observable private scrollToIndex: number = NO_SCROLL
  private listRef: any = null
  private readonly cellMeasurerCache: CellMeasurerCache = null

  public constructor(props: IProps) {
    super(props)

    this.scrollToIndex = props.scrollToIndex
    this.cellMeasurerCache = new CellMeasurerCache({
      defaultHeight: 100,
      fixedWidth: true,
    })
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (
      !areArraysEqual(this.props.rows, prevProps.rows) ||
      this.props.groupingKey !== prevProps.groupingKey
    ) {
      this.recomputeGridSize()
    }

    if (this.props.scrollToIndex !== prevProps.scrollToIndex) {
      runInAction(() => {
        this.scrollToIndex = this.props.scrollToIndex
      })
    }
  }

  public render() {
    const { rows } = this.props
    const { scrollToIndex } = this // weird, but only this way it works

    return (
      <AutoSizer>
        {({ width, height }) => (
          <List
            deferredMeasurementCache={this.cellMeasurerCache}
            data={rows}
            width={width}
            height={height}
            ref={this.setListRef}
            rowCount={rows.length}
            onScroll={this.onScroll}
            scrollToAlignment="start"
            overscanRowCount={OVERSCAN_ROW_COUNT}
            rowHeight={this.cellMeasurerCache.rowHeight}
            rowRenderer={this.rowRenderer}
            scrollToIndex={scrollToIndex}
            noRowsRenderer={this.noRowsRenderer}
            className="compact-deliveries-list"
          />
        )}
      </AutoSizer>
    )
  }

  private noRowsRenderer = () => {
    return (
      <div className="row text large pa12">
        <div>{deliveriesMapViewDescriptions.noDeliveries}</div>
        <FilterXIcon
          className="tw:size-4 tw:shrink-0 tw:cursor-pointer tw:text-muted-foreground"
          onClick={this.props.onFilterResetClick}
        />
      </div>
    )
  }

  private rowRenderer = ({ index, key, style, parent }: any) => {
    const { category, delivery } = this.props.rows[index]

    return (
      <CellMeasurer
        cache={this.cellMeasurerCache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {({ registerChild, measure }) => (
          <div style={style} ref={registerChild} className="col">
            {category
              ? this.renderCategoryRow(category)
              : this.renderDeliveryRow(delivery, measure)}
          </div>
        )}
      </CellMeasurer>
    )
  }

  private renderCategoryRow = ({
    categoryId,
    shortCategoryLabel,
    instancesInfo,
  }: ILWFCCategory) => {
    const { collapsedCategories, onCategoryCollapsingToggle } = this.props

    return (
      <GroupHeader>
        <GroupHeaderTrigger
          expanded={!collapsedCategories.get(categoryId)}
          onClick={() => onCategoryCollapsingToggle(categoryId)}
          ariaLabel={shortCategoryLabel}
          label={shortCategoryLabel}
          count={instancesInfo}
        />
      </GroupHeader>
    )
  }

  private renderDeliveryRow = (
    delivery: Delivery,
    measureSizeFn: () => void,
  ) => {
    const { highlightedIds } = this.props

    const isDeliveryActive = this.isDeliveryActive(delivery.id)

    return (
      <div
        className={classList({
          'bb-palette-brand-lighter card-wrapper full-height': true,
          highlight: isDeliveryActive || highlightedIds.includes(delivery.id),
          'active-delivery': isDeliveryActive,
        })}
      >
        <DeliveryWorkflowCard
          className="full-height"
          delivery={delivery}
          onClick={this.onDeliveryCardClick}
          measureSizeFn={measureSizeFn}
        />
      </div>
    )
  }

  @action
  private isDeliveryActive(deliveryId: string): boolean {
    return this.props.activeDeliveryId === deliveryId
  }

  private recomputeGridSize = () => {
    this.listRef?.recomputeGridSize()
    this.cellMeasurerCache?.clearAll()
  }

  private onScroll = () => {
    Promise.resolve().then(() => {
      runInAction(() => {
        this.scrollToIndex = NO_SCROLL
      })
    })
  }

  private setListRef = ref => (this.listRef = ref)

  private onDeliveryCardClick = (delivery: Delivery) => {
    this.props.onCardClick(delivery.id)
  }
}
